import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import axiosInstance from '../api/axios';
import { Button } from '../components/ui/button';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

// Three visual templates a brand can pick from. Kept simple but distinctly
// different so the admin sees real variation.
const TEMPLATES = {
  showcase: 'showcase',   // big hero, edge-to-edge grid
  minimal: 'minimal',     // white space, centered, soft
  elegant: 'elegant',     // dark band, serif headlines
};

const BrandPage = () => {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/brands/${slug}`);
        if (alive) {
          setBrand(res.data.brand);
          setProducts(res.data.products || []);
        }
      } catch (e) {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#8B7355]">Lädt …</div>;
  if (error || !brand) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <ShoppingBag className="h-12 w-12 text-[#C4A084] mb-4" />
        <h1 className="text-2xl font-semibold text-[#3D3D3D] mb-2">Marke nicht gefunden</h1>
        <p className="text-[#8B7355] mb-6">Diese Marke existiert nicht oder ist nicht aktiv.</p>
        <Link to="/products"><Button className="bg-[#C4A084] hover:bg-[#B89968] text-white rounded-none">Zurück zum Sortiment</Button></Link>
      </div>
    );
  }

  const template = TEMPLATES[brand.layout_template] || TEMPLATES.showcase;
  const accent = brand.accent_color || '#C4A084';

  return (
    <div className="min-h-screen" data-testid={`brand-page-${slug}`}>
      <BrandHero brand={brand} template={template} accent={accent} />
      <BrandProductGrid products={products} template={template} accent={accent} />
    </div>
  );
};

// ============== HERO ==============
const BrandHero = ({ brand, template, accent }) => {
  if (template === 'minimal') {
    return (
      <section className="bg-white py-16 sm:py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          {brand.logo_url && (
            <img src={brand.logo_url} alt={brand.name} className="h-16 sm:h-24 mx-auto mb-6 object-contain" />
          )}
          <h1 className="text-3xl sm:text-5xl font-light tracking-wide text-[#3D3D3D]" data-testid="brand-name">{brand.name}</h1>
          {brand.tagline && <p className="mt-3 text-lg italic" style={{ color: accent }}>{brand.tagline}</p>}
          {brand.description && <p className="mt-6 text-base text-[#5D4E3C] whitespace-pre-line leading-relaxed">{brand.description}</p>}
          <div className="mt-8 h-px w-24 mx-auto" style={{ backgroundColor: accent }} />
        </div>
      </section>
    );
  }
  if (template === 'elegant') {
    return (
      <section className="relative text-white overflow-hidden" style={{ backgroundColor: '#1F1A14' }}>
        {brand.hero_image && (
          <div className="absolute inset-0 opacity-30">
            <img src={brand.hero_image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative py-20 sm:py-28 px-6 max-w-5xl mx-auto">
          {brand.logo_url && (
            <img src={brand.logo_url} alt={brand.name} className="h-12 sm:h-16 mb-6 object-contain brightness-0 invert" />
          )}
          <h1 className="font-serif text-4xl sm:text-6xl leading-tight" data-testid="brand-name">{brand.name}</h1>
          {brand.tagline && <p className="mt-3 text-xl font-light" style={{ color: accent }}>{brand.tagline}</p>}
          {brand.description && <p className="mt-8 text-base text-white/80 max-w-2xl whitespace-pre-line leading-relaxed">{brand.description}</p>}
        </div>
      </section>
    );
  }
  // SHOWCASE — big hero with accent band
  return (
    <section className="relative" style={{ backgroundColor: accent }}>
      {brand.hero_image && (
        <div className="absolute inset-0">
          <img src={brand.hero_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: `${accent}99` }} />
        </div>
      )}
      <div className="relative py-16 sm:py-24 px-6 max-w-6xl mx-auto text-white">
        <div className="flex items-start gap-6 flex-col sm:flex-row sm:items-center">
          {brand.logo_url && (
            <img src={brand.logo_url} alt={brand.name} className="h-20 sm:h-28 object-contain bg-white/95 p-3 rounded" />
          )}
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="brand-name">{brand.name}</h1>
            {brand.tagline && <p className="mt-2 text-lg sm:text-xl text-white/90">{brand.tagline}</p>}
          </div>
        </div>
        {brand.description && <p className="mt-8 text-base sm:text-lg max-w-3xl whitespace-pre-line leading-relaxed text-white/95">{brand.description}</p>}
      </div>
    </section>
  );
};

// ============== PRODUCT GRID ==============
const BrandProductGrid = ({ products, template, accent }) => {
  if (!products.length) {
    return (
      <div className="py-20 text-center text-[#8B7355]">
        <ShoppingBag className="h-10 w-10 mx-auto mb-3 opacity-60" />
        Aktuell noch keine Artikel. Bald ist hier mehr zu entdecken.
      </div>
    );
  }
  const isMinimal = template === 'minimal';
  return (
    <section className={isMinimal ? 'bg-[#FAF5EE] py-12 px-4 sm:px-8' : 'bg-white py-12 px-4 sm:px-8'}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#3D3D3D] mb-8" data-testid="brand-products-heading">
          <span className="border-b-2 pb-1" style={{ borderColor: accent }}>Unsere Stücke</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="brand-products-grid">
          {products.map((p) => (
            <Link
              key={p.id}
              to={`/products/${p.id}`}
              className="group block bg-white shadow-sm hover:shadow-md transition-shadow"
              data-testid={`brand-product-${p.id}`}
            >
              <div className="aspect-square bg-[#FAF5EE] overflow-hidden">
                {p.images?.[0] ? (
                  <img src={imgVariant(p.images[0], { w: 400 })} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#C4A084]"><ShoppingBag className="h-10 w-10" /></div>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-[#3D3D3D] line-clamp-2 min-h-[2.5rem]">{p.name}</p>
                <p className="mt-1 text-base font-semibold" style={{ color: accent }}>{formatEUR(Number(p.price))}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandPage;
